import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Layout, Button, Input, Modal, Spin, Empty, Popconfirm, Flex, Tooltip, Steps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBinoculars, faTrash, faArrowsRotate, faPrint } from '@fortawesome/pro-light-svg-icons';

import { SideMenu } from 'layout/SideMenu';
import { MaskableComponent } from 'components/Tutorial/MaskableComponent';
import UIContext from 'components/Utils/UIContext';
import { WatchReportView } from './WatchReportView';
import { AttachmentsSection } from 'components/AttachmentsSection';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
	useGetWatchReportsQuery,
	useGenerateWatchReportLazyQuery,
	useDeleteWatchReportMutation
} from '../../../graph/dist/generated';
import { AI_GENERATION_ENABLED } from 'config/featureFlags';
import { useParams } from 'react-router';
import { useScenarioController } from 'hooks/useScenarioController';

import './Watch.sass';

const POLL_WHILE_RUNNING_MS = 10_000;

export const Watch = (props: any) => {
	const { t, i18n } = useTranslation();
	const uiContext: any = useContext(UIContext);
	const [openedReport, setOpenedReport] = useState<any>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [watchContext, setWatchContext] = useState('');

	const params: any = useParams();
	const { createScenario } = useScenarioController();

	const { data, refetch } = useGetWatchReportsQuery({
		variables: { projectId: props.id },
		skip: !props.id,
		fetchPolicy: 'cache-and-network'
	});

	// Query côté schéma (pattern analyzeSituation) — network-only, jamais du cache.
	const [generateWatchReport, { loading: launching }] = useGenerateWatchReportLazyQuery({ fetchPolicy: 'network-only' });
	const [deleteWatchReport] = useDeleteWatchReportMutation();

	const reports = useMemo(() => {
		const list = [...(data?.getWatchReports || [])];
		return list.sort((a: any, b: any) => (b?.createdAt || '').localeCompare(a?.createdAt || ''));
	}, [data]);

	const runningReport = reports.find((r: any) => r?.status === 'running');
	const currentReport = reports.find((r: any) => r?.status === 'completed');
	const archive = reports.filter((r: any) => r?.status === 'completed' && r !== currentReport);

	// La génération dure plusieurs minutes : tant qu'un rapport est `running`,
	// on re-demande la liste périodiquement (la notification realtime peut
	// arriver sur un autre onglet / être manquée).
	useEffect(() => {
		if (!runningReport) return;
		const timer = setInterval(() => refetch(), POLL_WHILE_RUNNING_MS);
		return () => clearInterval(timer);
	}, [runningReport, refetch]);

	const handleGenerate = async () => {
		setConfirmOpen(false);
		await generateWatchReport({
			variables: {
				projectId: props.id,
				language: i18n.language?.slice(0, 2) || 'fr',
				context: watchContext.trim() || undefined
			}
		});
		refetch();
	};

	const handleGenerateScenario = async (reportTitle: string) => {
		if (!AI_GENERATION_ENABLED || !props.pushModal) return;
		const { default: ScenarioEditionModal } = await import('components/Modal/ScenarioEditionModal');
		props.pushModal(
			<ScenarioEditionModal
				projectId={params.id || props.id}
				onHide={() => props.popModal()}
				onSave={async (values: any) => {
					if (values?.aiGenerate && values?.hypothesis && values?.horizon) {
						const aiResult = await createScenario(
							params.id || props.id,
							props,
							null,
							{ name: values.name, description: values.description, impacts: values.impacts },
							() => {}
						);
						if (props.refetch) await props.refetch();
						if (!aiResult?.scenarioId) return;
						const { default: AIScenarioGenerationModal } = await import('components/Modal/AIScenarioGenerationModal');
						props.replaceModal(
							<AIScenarioGenerationModal
								visible={true}
								projectId={params.id || props.id}
								scenarioId={aiResult.scenarioId}
								scenarioName={values.name}
								hypothesis={values.hypothesis}
								horizon={values.horizon}
								scenario={{ id: aiResult.scenarioId, name: values.name }}
								stakeholders={props.stakeholders || []}
								stakes={props.stakes || []}
								risks={props.risks || []}
								indicators={props.indicators || []}
								scenarios={props.scenarios || []}
								fishboneBranchs={props.fishboneBranchs || []}
								likelihoods={props.likelihoods || []}
								impacts={props.impacts || []}
								onClose={() => props.popModal()}
								onComplete={async () => {
									if (props.refetch) await props.refetch();
									props.popModal();
								}}
							/>
						);
						return;
					}
					await createScenario(params.id || props.id, props, null, values, () => {});
					props.popModal();
				}}
				id={false}
				values={{
					name: reportTitle || t('pages.watch.title'),
					description: '',
					impacts: ''
				}}
			/>
		);
	};

	const handleDelete = async (report: any) => {
		await deleteWatchReport({ variables: { id: report.id } });
		if (openedReport?.id === report.id) setOpenedReport(null);
		refetch();
	};

	const reportDate = (r: any) =>
		dayjs(r?.completedAt || r?.createdAt).format('DD/MM/YYYY HH:mm');

	const printReport = () => window.print();

	const renderReport = (report: any) => (
		<WatchReportView
			report={report}
			onGenerateScenario={AI_GENERATION_ENABLED && uiContext.canEdit ? handleGenerateScenario : undefined}
			meta={
				<div className="watch-report-meta">
					<div className="watch-report-meta-infos">
						<span>{t('pages.watch.generated_on')} {reportDate(report)}</span>
						{report.neutralSubject && (
							<Tooltip title={report.neutralSubject} placement="bottom">
								<span className="watch-neutral-subject">
									{t('pages.watch.messages.neutral_subject')}
								</span>
							</Tooltip>
						)}
					</div>
					<div className="watch-report-meta-actions">
						<Tooltip title={t('pages.watch.actions.print')}>
							<Button size="small" type="text" onClick={printReport}>
								<FontAwesomeIcon icon={faPrint} />
							</Button>
						</Tooltip>
						{uiContext.canEdit && <Popconfirm
							title={t('pages.watch.actions.delete_confirm')}
							onConfirm={() => handleDelete(report)}
						>
							<Tooltip title={t('pages.watch.actions.delete')}>
								<Button size="small" type="text">
									<FontAwesomeIcon icon={faTrash} />
								</Button>
							</Tooltip>
						</Popconfirm>}
					</div>
				</div>
			}
		/>
	);

	// Version print : rapport affiché (modale prioritaire) rendu LINÉAIREMENT
	// (toutes sections, sans onglets) — seule visible en @media print.
	const printedReport = openedReport || currentReport;

	return (
		<Layout className="Watch w-100" data-testid="watch-page">
			<Layout.Content className="Body pt-1">
				<MaskableComponent masked={uiContext.maskedZones['main']}>
					<div className="watch-center" data-testid="watch-content">
						{runningReport && (() => {
							// Étape déduite de l'item : le sujet neutralisé n'est écrit
							// qu'à la fin de l'anonymisation.
							const current = runningReport.neutralSubject ? 1 : 0;
							const minutes = Math.max(0, Math.round(dayjs().diff(dayjs(runningReport.createdAt), 'minute')));
							return (
								<div className="watch-running" data-testid="watch-running">
									<Steps
										size="small"
										current={current}
										items={[
											{ title: t('pages.watch.steps.anonymizing') },
											{
												title: t('pages.watch.steps.researching'),
												description: current === 1 ? t('pages.watch.steps.elapsed', { minutes }) : undefined,
												icon: current === 1 ? <Spin size="small" /> : undefined
											},
											{ title: t('pages.watch.steps.done') }
										]}
									/>
									{runningReport.neutralSubject && (
										<div className="watch-running-subject">
											{t('pages.watch.messages.neutral_subject')} : {runningReport.neutralSubject}
										</div>
									)}
								</div>
							);
						})()}

						{!runningReport && reports.find((r: any) => r?.status === 'failed') && !currentReport && (
							<div className="watch-failed">
								{t('pages.watch.messages.failed')}
							</div>
						)}

						{currentReport
							? renderReport(currentReport)
							: !runningReport && (
								<Empty description={t('pages.watch.empty')}>
									{uiContext.canEdit && <Button type="primary" loading={launching} onClick={() => setConfirmOpen(true)}>
										<FontAwesomeIcon icon={faBinoculars} className="mr-05" />
										{t('pages.watch.actions.generate')}
									</Button>}
								</Empty>
							)}
					</div>
				</MaskableComponent>
			</Layout.Content>

			<SideMenu
				defaultTab="watch"
				tabs={[{ id: 'watch', title: t('pages.watch.title') }]}
			>
				{() => (
					<div className="watch-sidemenu p-1">
						{uiContext.canEdit && <Button
							block
							type="primary"
							className="watch-generate"
							loading={launching || !!runningReport}
							disabled={!!runningReport}
							data-testid="watch-generate-button"
							onClick={() => setConfirmOpen(true)}
						>
							{!launching && !runningReport && (
								<FontAwesomeIcon icon={faArrowsRotate} className="mr-05" />
							)}
							{runningReport
								? t('pages.watch.actions.generating')
								: t('pages.watch.actions.generate_new')}
						</Button>}

						<h3 className="mt-2">{t('pages.watch.archive')}</h3>
						{!archive.length && (
							<div className="watch-archive-empty">{t('pages.watch.empty')}</div>
						)}
						<ul className="watch-archive-list" data-testid="watch-archive">
							{archive.map((r: any) => (
								<li key={r.id}>
									<a onClick={() => setOpenedReport(r)}>
										<span className="watch-archive-date">{reportDate(r)}</span>
										<span className="watch-archive-title">
											{r.title || t('pages.watch.title')}
										</span>
									</a>
									{uiContext.canEdit && <Popconfirm
										title={t('pages.watch.actions.delete_confirm')}
										onConfirm={() => handleDelete(r)}
									>
										<Button className="mini" type="text" size="small">
											<FontAwesomeIcon icon={faTrash} />
										</Button>
									</Popconfirm>}
								</li>
							))}
						</ul>
					</div>
				)}
			</SideMenu>

			{/* Confirmation : rappelle que le sujet part anonymisé vers Perplexity */}
			<Modal
				open={confirmOpen}
				className="watch-confirm-modal"
				title={t('pages.watch.messages.confirm_title')}
				onOk={handleGenerate}
				onCancel={() => setConfirmOpen(false)}
				okText={t('pages.watch.actions.generate')}
			>
				<p>{t('pages.watch.messages.confirm_body')}</p>
				<div style={{ marginTop: 12 }}>
					<div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
						{t('pages.watch.messages.context_label', { defaultValue: 'Angles spécifiques à couvrir (optionnel)' })}
					</div>
					<Input.TextArea
						rows={3}
						placeholder={t('pages.watch.messages.context_placeholder', { defaultValue: 'Ex : impact sur nos partenaires européens, évolutions réglementaires…' })}
						value={watchContext}
						onChange={(e) => setWatchContext(e.target.value)}
						maxLength={500}
						showCount
					/>
					<div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
						{t('pages.watch.messages.context_hint', { defaultValue: 'Ce texte est aussi anonymisé avant d\'être transmis.' })}
					</div>
				</div>
				<div style={{ marginTop: 20, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
					<div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
						{t('pages.watch.messages.attachments_label', { defaultValue: 'Documents internes du projet (optionnel)' })}
					</div>
					<AttachmentsSection projectId={props.id} disabled={!uiContext.canEdit} />
					<div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
						{t('pages.watch.messages.attachments_hint', { defaultValue: 'Les documents ajoutés ici sont analysés en privé par l\'IA, sans quitter votre compte AWS.' })}
					</div>
				</div>
			</Modal>

			{/* Détail d'un rapport d'archive : modale quasi plein écran */}
			<Modal
				open={!!openedReport}
				onCancel={() => setOpenedReport(null)}
				footer={null}
				width="92vw"
				style={{ top: 20 }}
				className="watch-report-modal"
				title={openedReport ? (openedReport.title || t('pages.watch.title')) : ''}
			>
				<div className="watch-report-modal-body">
					{openedReport && renderReport(openedReport)}
				</div>
			</Modal>

			{printedReport && createPortal(
				<div className="watch-print-only">
					<div className="watch-report-print-meta">
						<strong>{printedReport.title || t('pages.watch.title')}</strong>
						{' — '}
						{reportDate(printedReport)}
					</div>
					<div className="watch-report">
						<ReactMarkdown remarkPlugins={[remarkGfm]}>{printedReport.content || ''}</ReactMarkdown>
					</div>
				</div>,
				document.body
			)}
		</Layout>
	);
};

export default Watch;
